import React, { useState } from 'react';
import '../../styles/sizeGuideModal.css';

const SizeGuideModal = ({ isOpen, onClose, product }) => {
  const [unit, setUnit] = useState('cm'); // 'cm' | 'in'

  if (!isOpen) return null;

  const category = (product?.category || 'sofa').toLowerCase();

  // Category-specific architectural dimensions
  const getDimensionData = () => {
    switch (category) {
      case 'chair':
        return {
          title: 'Lounge & Armchair Dimensions',
          width: unit === 'cm' ? '88 cm' : '34.6 in',
          depth: unit === 'cm' ? '82 cm' : '32.3 in',
          height: unit === 'cm' ? '78 cm' : '30.7 in',
          seatHeight: unit === 'cm' ? '42 cm' : '16.5 in',
          armHeight: unit === 'cm' ? '58 cm' : '22.8 in',
          weight: unit === 'cm' ? '28 kg' : '61.7 lbs',
          doorwayMin: unit === 'cm' ? '75 cm' : '29.5 in',
        };
      case 'table':
        return {
          title: 'Dining & Accent Table Dimensions',
          width: unit === 'cm' ? '220 cm' : '86.6 in',
          depth: unit === 'cm' ? '100 cm' : '39.4 in',
          height: unit === 'cm' ? '76 cm' : '29.9 in',
          seatHeight: 'N/A',
          armHeight: 'N/A',
          weight: unit === 'cm' ? '85 kg' : '187 lbs',
          doorwayMin: unit === 'cm' ? '85 cm' : '33.5 in',
        };
      case 'lighting':
        return {
          title: 'Architectural Lighting Dimensions',
          width: unit === 'cm' ? '45 cm' : '17.7 in',
          depth: unit === 'cm' ? '45 cm' : '17.7 in',
          height: unit === 'cm' ? '165 cm' : '65.0 in',
          seatHeight: 'N/A',
          armHeight: 'N/A',
          weight: unit === 'cm' ? '12 kg' : '26.5 lbs',
          doorwayMin: unit === 'cm' ? '50 cm' : '19.7 in',
        };
      case 'sofa':
      default:
        return {
          title: 'Grand Living Sofa Dimensions',
          width: unit === 'cm' ? '240 cm' : '94.5 in',
          depth: unit === 'cm' ? '105 cm' : '41.3 in',
          height: unit === 'cm' ? '78 cm' : '30.7 in',
          seatHeight: unit === 'cm' ? '44 cm' : '17.3 in',
          armHeight: unit === 'cm' ? '60 cm' : '23.6 in',
          weight: unit === 'cm' ? '72 kg' : '158.7 lbs',
          doorwayMin: unit === 'cm' ? '82 cm' : '32.3 in',
        };
    }
  };

  const dim = getDimensionData();

  return (
    <div className="size-guide-modal__backdrop" onClick={onClose}>
      <div className="size-guide-modal__dialog" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="size-guide-modal__header">
          <div>
            <span className="size-guide-modal__kicker">ATELIER MEASUREMENTS SPECIFICATION</span>
            <h3 className="size-guide-modal__title">
              <i className="ri-ruler-2-line"></i> Dimensions & Architectural Space Guide
            </h3>
            <p className="size-guide-modal__piece-name">{product?.productName}</p>
          </div>
          <button type="button" className="size-guide-modal__close-btn" onClick={onClose} aria-label="Close">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="size-guide-modal__body">
          {/* Unit Toggle */}
          <div className="size-guide-modal__unit-toggle">
            <span>Unit of Measurement:</span>
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${unit === 'cm' ? 'btn-warning fw-bold' : 'btn-outline-light text-dark'}`}
                onClick={() => setUnit('cm')}
              >
                Metric (Centimeters)
              </button>
              <button
                type="button"
                className={`btn btn-sm ${unit === 'in' ? 'btn-warning fw-bold' : 'btn-outline-light text-dark'}`}
                onClick={() => setUnit('in')}
              >
                Imperial (Inches)
              </button>
            </div>
          </div>

          {/* Dimension Grid */}
          <div className="size-guide-modal__grid">
            <div className="size-guide-modal__cell">
              <span className="size-guide-modal__cell-label">Overall Width</span>
              <strong className="size-guide-modal__cell-val">{dim.width}</strong>
            </div>

            <div className="size-guide-modal__cell">
              <span className="size-guide-modal__cell-label">Overall Depth</span>
              <strong className="size-guide-modal__cell-val">{dim.depth}</strong>
            </div>

            <div className="size-guide-modal__cell">
              <span className="size-guide-modal__cell-label">Overall Height</span>
              <strong className="size-guide-modal__cell-val">{dim.height}</strong>
            </div>

            <div className="size-guide-modal__cell">
              <span className="size-guide-modal__cell-label">Seat Height</span>
              <strong className="size-guide-modal__cell-val">{dim.seatHeight}</strong>
            </div>

            <div className="size-guide-modal__cell">
              <span className="size-guide-modal__cell-label">Armrest Height</span>
              <strong className="size-guide-modal__cell-val">{dim.armHeight}</strong>
            </div>

            <div className="size-guide-modal__cell">
              <span className="size-guide-modal__cell-label">Piece Net Weight</span>
              <strong className="size-guide-modal__cell-val">{dim.weight}</strong>
            </div>
          </div>

          {/* Delivery & Passage Verification Box */}
          <div className="size-guide-modal__doorway-card">
            <div className="d-flex align-items-center gap-3">
              <div className="size-guide-modal__doorway-icon">
                <i className="ri-door-closed-line"></i>
              </div>
              <div>
                <h5 className="mb-1 text-dark fw-bold">Delivery Passage Guarantee</h5>
                <p className="mb-0 text-muted small">
                  Requires a minimum hallway/entry door clearance of <strong>{dim.doorwayMin}</strong>.
                  Our White-Glove team will unbox, assemble, and position the piece in your sanctuary.
                </p>
              </div>
            </div>
          </div>

          {/* Bespoke Tailoring CTA */}
          <div className="size-guide-modal__custom-cta">
            <div>
              <strong className="d-block text-dark">Need Bespoke Custom Dimensions?</strong>
              <span className="text-muted small">
                Our master artisans can modify dimensions, fabrics, and solid wood species to your exact architectural blueprints.
              </span>
            </div>
            <a
              href="https://wa.me/201000000000?text=Hello%20L'Habilleur,%20I%20am%20interested%20in%20a%20bespoke%20dimension%20consultation"
              target="_blank"
              rel="noreferrer"
              className="btn btn-dark btn-sm px-3 text-nowrap"
            >
              <i className="ri-whatsapp-line me-1 text-success"></i> Bespoke Inquiry
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SizeGuideModal;
